import {Box, Button, HStack, Text, VStack} from '@chakra-ui/react';
import {UserAvatar} from '../UserAvatar';
import {Check, History, Reply, RotateCcw, ScanEye} from 'lucide-react';
import {DriveComment, DriveReply, DriveUser} from '../../drive/types';
import {formatDisplayDateTime} from '../../domain/naming';
import {formatRelativeTime, getVisibleReplies, normalizePhotoLink} from '../../domain/comments';
import {AnchorRect, AnchorVersion, parseCommentAnchor, rectAspectRatio} from '../../domain/commentAnchor';
import {CommentedPicture} from './useCommentAnchoring';

interface CommentThreadCardProps {
    comment: DriveComment;
    isReplyTarget: boolean;
    isResolving: boolean;
    onReply: () => void;
    onResolve: () => void;
    /** For a preview of the region the comment is anchored to. */
    picture?: CommentedPicture;
    /** The comment's region is shown on the picture. */
    regionShown?: boolean;
    /** Shows or hides the region on the picture; no button without it. */
    onToggleRegion?: () => void;
    /** The version of the picture the region was drawn on (`anchorVersion`); unknown counts as current. */
    regionVersion?: AnchorVersion;
    /** That older version is the one the viewer shows now. */
    viewingRegionVersion?: boolean;
    /** Shows the older version the region was drawn on, with the region on it; no button without it. */
    onViewRegionVersion?: () => void;
}

export function CommentThreadCard({
    comment,
    isReplyTarget,
    isResolving,
    onReply,
    onResolve,
    picture,
    regionShown,
    onToggleRegion,
    regionVersion,
    viewingRegionVersion,
    onViewRegionVersion,
}: CommentThreadCardProps) {
    const replies = getVisibleReplies(comment);
    const region = parseCommentAnchor(comment.anchor)?.rect;

    return <Box
        bg="whiteAlpha.50"
        borderWidth="1px"
        borderColor={isReplyTarget ? 'blue.300' : 'whiteAlpha.100'}
        boxShadow={isReplyTarget ? '0 0 0 1px var(--chakra-colors-blue-300)' : undefined}
        borderRadius="lg"
        p={3}
        transition="border-color 0.15s, box-shadow 0.15s"
    >
        <CommentMessage author={comment.author} createdTime={comment.createdTime} content={comment.content}/>
        {region && picture?.url &&
            <RegionPreview
                region={region}
                picture={picture}
                url={picture.url}
                shown={!!regionShown}
                onClick={onToggleRegion}
            />
        }
        {region && regionVersion && regionVersion.kind !== 'current' &&
            <RegionVersionNote
                version={regionVersion}
                viewing={!!viewingRegionVersion}
                onView={onViewRegionVersion}
            />
        }

        {replies.length > 0 &&
            <VStack align="stretch" gap={3} mt={3} ml={3} pl={3} borderLeftWidth="2px" borderColor="whiteAlpha.200">
                {replies.map((reply) => <ReplyMessage key={reply.id} reply={reply}/>)}
            </VStack>
        }

        <HStack gap={1} mt={2} justify="flex-end">
            {region && onToggleRegion &&
                <Button
                    size="xs"
                    variant="ghost"
                    mr="auto"
                    color={regionShown ? 'orange.300' : 'gray.300'}
                    bg={regionShown ? 'whiteAlpha.200' : undefined}
                    _hover={{bg: 'whiteAlpha.200', color: regionShown ? 'orange.200' : 'white'}}
                    aria-pressed={regionShown}
                    title={regionShown ? 'A kijelölés elrejtése a képről' : 'A megjegyzés kijelölésének megjelenítése a képen'}
                    onClick={onToggleRegion}
                >
                    <ScanEye size={14}/>
                    Kijelölés
                </Button>
            }
            <Button
                size="xs"
                variant="ghost"
                color={isReplyTarget ? 'blue.200' : 'gray.300'}
                _hover={{bg: 'whiteAlpha.200', color: 'white'}}
                onClick={onReply}
            >
                <Reply size={14}/>
                Válasz
            </Button>
            <Button
                size="xs"
                variant="ghost"
                color="green.300"
                _hover={{bg: 'green.900', color: 'green.200'}}
                onClick={onResolve}
                loading={isResolving}
                title="A beszélgetés lezárása"
            >
                <Check size={14}/>
                Lezárás
            </Button>
        </HStack>
    </Box>;
}

// The tallest a region's preview gets; wide regions get as wide as the card, and lower.
const PREVIEW_HEIGHT = 80;

interface RegionPreviewProps {
    region: AnchorRect;
    picture: CommentedPicture;
    url: string;
    shown: boolean;
    onClick?: () => void;
}

/** The part of the picture a comment is anchored to, cropped from the image the viewer already loaded. */
function RegionPreview({region, picture, url, shown, onClick}: RegionPreviewProps) {
    const ratio = rectAspectRatio(region, picture.size);
    return <Box
        as={onClick ? 'button' : 'div'}
        display="block"
        position="relative"
        overflow="hidden"
        mt={2}
        ml="34px"
        maxW="calc(100% - 34px)"
        minW={6}
        borderRadius="md"
        borderWidth="1px"
        borderColor={shown ? 'orange.300' : 'whiteAlpha.300'}
        bg="blackAlpha.500"
        cursor={onClick ? 'pointer' : undefined}
        _hover={onClick ? {borderColor: shown ? 'orange.200' : 'whiteAlpha.600'} : undefined}
        style={{width: `${PREVIEW_HEIGHT * ratio}px`, aspectRatio: String(ratio)}}
        onClick={onClick}
        aria-label={onClick ? (shown ? 'A kijelölés elrejtése a képről' : 'A kijelölés megjelenítése a képen') : undefined}
        title={onClick ? (shown ? 'A kijelölés elrejtése a képről' : 'A kijelölés megjelenítése a képen') : undefined}
    >
        <img
            src={url}
            alt=""
            draggable={false}
            referrerPolicy="no-referrer"
            style={{
                position: 'absolute',
                maxWidth: 'none',
                width: `${100 / region.w}%`,
                height: `${100 / region.h}%`,
                left: `${-region.x / region.w * 100}%`,
                top: `${-region.y / region.h * 100}%`,
            }}
        />
    </Box>;
}

interface RegionVersionNoteProps {
    version: Exclude<AnchorVersion, { kind: 'current' }>;
    viewing: boolean;
    onView?: () => void;
}

/** Says that a region was drawn on an older version of the picture, which it may not fit, with a way to see it there. */
function RegionVersionNote({version, viewing, onView}: RegionVersionNoteProps) {
    return <HStack align="flex-start" gap={1.5} mt={1.5} ml="16px" fontSize="xs" color="yellow.200">
        <Box mt="3px" flexShrink={0}><History size={12}/></Box>
        {version.kind === 'older'
            ? <Text flex={1}>
                A(z) #{version.revision.number}. verzión jelölték ki
                {viewing && <Text as="span" color="gray.400"> · most ez látható</Text>}
            </Text>
            : <Text flex={1} title="A Drive egy idő után törli a régi verziókat">Egy korábbi, törölt verzión jelölték ki</Text>
        }
        {version.kind === 'older' && !viewing && onView &&
            <Button
                size="xs"
                h={5}
                px={1.5}
                mt="-2px"
                flexShrink={0}
                variant="ghost"
                color="yellow.200"
                _hover={{bg: 'whiteAlpha.200', color: 'yellow.100'}}
                title={`A kép #${version.revision.number}. verziójának megtekintése a kijelöléssel`}
                onClick={onView}
            >
                Megtekintés
            </Button>
        }
    </HStack>;
}

function ReplyMessage({reply}: { reply: DriveReply }) {
    if (!reply.content?.trim() && reply.action === 'reopen') {
        return <HStack gap={1.5} color="gray.400" fontSize="xs">
            <RotateCcw size={12}/>
            <Text>
                <Text as="span" fontWeight="semibold">{authorName(reply.author)}</Text> újranyitotta
                · {formatRelativeTime(reply.createdTime)}
            </Text>
        </HStack>;
    }
    return <CommentMessage author={reply.author} createdTime={reply.createdTime} content={reply.content} compact/>;
}

interface CommentMessageProps {
    author: DriveUser | undefined;
    createdTime: string;
    content: string | undefined;
    compact?: boolean;
}

function CommentMessage({author, createdTime, content, compact}: CommentMessageProps) {
    const name = authorName(author);
    const created = new Date(createdTime);
    return <HStack align="flex-start" gap={2.5}>
        <UserAvatar
            size={compact ? '2xs' : 'xs'}
            name={name}
            src={normalizePhotoLink(author?.photoLink)}
            mt={0.5}
            bg="blue.600"
            color="white"
        />
        <Box flex={1} minW={0}>
            <HStack gap={1.5} align="baseline">
                <Text fontSize="sm" fontWeight="semibold" lineClamp={1}>
                    {name}{author?.me && <Text as="span" color="gray.400" fontWeight="normal"> (te)</Text>}
                </Text>
                <Text
                    fontSize="xs"
                    color="gray.400"
                    flexShrink={0}
                    title={Number.isNaN(created.getTime()) ? undefined : formatDisplayDateTime(created)}
                >
                    {formatRelativeTime(createdTime)}
                </Text>
            </HStack>
            <Text fontSize="sm" color="gray.100" whiteSpace="pre-wrap" wordBreak="break-word">{content}</Text>
        </Box>
    </HStack>;
}

function authorName(author: DriveUser | undefined): string {
    return author?.displayName || 'Ismeretlen felhasználó';
}
